import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import type { StudioId } from '@/domain/types';

const MAX_COMPARE = 3;

interface Props {
  selected: readonly StudioId[];
  onChange: (next: StudioId[]) => void;
}

/** Reporting studio filter. Empty selection = all studios (network). Selecting up
 *  to three studios drives side-by-side comparison. */
export default function StudioCompareFilter({ selected, onChange }: Props) {
  const studios = useBrainStore((s) => s.studios);
  const options = Object.values(studios).filter((s) => !s.archived);

  const toggle = (id: StudioId) => {
    if (selected.includes(id)) onChange(selected.filter((s) => s !== id));
    else if (selected.length < MAX_COMPARE) onChange([...selected, id]);
  };

  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="studio-compare-filter">
      <button
        type="button"
        onClick={() => onChange([])}
        data-testid="studio-compare-all"
        aria-pressed={selected.length === 0}
        className={clsx(
          'rounded-md px-2.5 py-1.5 text-xs font-inter transition border',
          selected.length === 0
            ? 'bg-gold/15 text-gold-bright border-gold-dim/60'
            : 'text-cream-soft border-gold-dim/20 hover:text-gold-bright',
        )}
      >
        All studios
      </button>
      {options.map((s) => {
        const on = selected.includes(s.id);
        const disabled = !on && selected.length >= MAX_COMPARE;
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => toggle(s.id)}
            disabled={disabled}
            aria-pressed={on}
            data-testid={`studio-compare-${s.id}`}
            className={clsx(
              'rounded-md px-2.5 py-1.5 text-xs font-inter transition border',
              on
                ? 'bg-gold/15 text-gold-bright border-gold-dim/60'
                : 'text-cream-soft border-gold-dim/20 hover:text-gold-bright',
              disabled && 'opacity-40 cursor-not-allowed',
            )}
          >
            {s.name}
          </button>
        );
      })}
    </div>
  );
}
